/**
 * Typed API endpoint functions for the Fantasy Stats Tracker.
 * Encapsulates all API calls with proper request/response types.
 */

import {
  User,
  ScoringProfile,
  FantasyTeam,
  RosterEntry,
  Player,
  PlayerDetail,
  PlayerTrends,
  Paginated,
  PlayerSearchParams,
  PlayerCompareParams,
  StatsLeadersParams,
  StatsWeeksParams,
  Freshness,
  HealthStatus,
  RosterTimeline,
  LineupOptimal,
  Game,
} from "../types/index";
import * as client from "./client";

// ============================================================================
// Auth
// ============================================================================

export async function register(
  email: string,
  password: string
): Promise<User> {
  return client.post<User>("/auth/register", { email, password });
}

export async function login(email: string, password: string): Promise<User> {
  return client.post<User>("/auth/login", { email, password });
}

export async function logout(): Promise<null> {
  return client.post<null>("/auth/logout", {});
}

export async function me(): Promise<User> {
  return client.get<User>("/auth/me");
}

// ============================================================================
// Players
// ============================================================================

/**
 * Search players with filters and pagination.
 * Properly handles repeatable query parameters: position and stat arrays.
 */
export async function searchPlayers(
  params: PlayerSearchParams
): Promise<Paginated<Player>> {
  const searchParams = new URLSearchParams();

  // Add simple scalar params
  if (params.q !== undefined && params.q !== "") {
    searchParams.append("q", params.q);
  }
  if (params.season !== undefined) {
    searchParams.append("season", String(params.season));
  }
  if (params.week !== undefined) {
    searchParams.append("week", String(params.week));
  }
  if (params.season_type !== undefined) {
    searchParams.append("season_type", params.season_type);
  }
  if (params.status !== undefined) {
    searchParams.append("status", params.status);
  }
  if (params.available !== undefined) {
    searchParams.append("available", String(params.available));
  }
  if (params.sort !== undefined && params.sort !== "") {
    searchParams.append("sort", params.sort);
  }
  if (params.scoring_profile_id !== undefined) {
    searchParams.append("scoring_profile_id", String(params.scoring_profile_id));
  }
  if (params.page !== undefined) {
    searchParams.append("page", String(params.page));
  }
  if (params.per_page !== undefined) {
    searchParams.append("per_page", String(params.per_page));
  }

  // Add repeatable params: position (array)
  if (params.position && params.position.length > 0) {
    params.position.forEach((pos) => {
      if (pos !== "") {
        searchParams.append("position", pos);
      }
    });
  }

  // Add repeatable params: team (array)
  if (params.team && params.team.length > 0) {
    params.team.forEach((t) => {
      if (t !== "") {
        searchParams.append("team", t);
      }
    });
  }

  // Add repeatable params: stat (array of "column:op:value")
  if (params.stat && params.stat.length > 0) {
    params.stat.forEach((s) => {
      if (s !== "") {
        searchParams.append("stat", s);
      }
    });
  }

  const queryString = searchParams.toString();
  const path = queryString ? `/players?${queryString}` : "/players";
  return client.get<Paginated<Player>>(path);
}

/**
 * Get a single player's detail with season totals, week-by-week stats, injuries, and snap counts.
 */
export async function getPlayerDetail(
  gsis_id: string,
  season?: number
): Promise<PlayerDetail> {
  const query = season !== undefined ? `?season=${season}` : "";
  return client.get<PlayerDetail>(`/players/${gsis_id}${query}`);
}

/**
 * Get player trends: rolling windows and year-over-year deltas.
 */
export async function getPlayerTrends(
  gsis_id: string,
  season?: number
): Promise<PlayerTrends> {
  const query = season !== undefined ? `?season=${season}` : "";
  return client.get<PlayerTrends>(`/players/${gsis_id}/trends${query}`);
}

/**
 * Compare up to 4 players side-by-side.
 * @param ids Array of GSIS IDs (max 4)
 */
export async function comparePlayers(
  ids: string[]
): Promise<PlayerDetail[]> {
  if (ids.length === 0 || ids.length > 4) {
    throw new Error("Compare requires 1-4 player IDs");
  }
  const idsParam = ids.join(",");
  return client.get<PlayerDetail[]>(`/players/compare?ids=${idsParam}`);
}

// ============================================================================
// Teams
// ============================================================================

/**
 * List all teams for the current user.
 */
export async function listTeams(
  page?: number,
  per_page?: number
): Promise<Paginated<FantasyTeam>> {
  const params = new URLSearchParams();
  if (page !== undefined) params.append("page", String(page));
  if (per_page !== undefined) params.append("per_page", String(per_page));
  const queryString = params.toString();
  const path = queryString ? `/teams?${queryString}` : "/teams";
  return client.get<Paginated<FantasyTeam>>(path);
}

/**
 * Create a new fantasy team.
 */
export async function createTeam(data: {
  name: string;
  season: number;
  scoring_profile_id: number;
  flex_instead_of_kicker?: boolean;
}): Promise<FantasyTeam> {
  return client.post<FantasyTeam>("/teams", data);
}

/**
 * Get a team by ID with current roster and weekly totals.
 */
export async function getTeam(id: number): Promise<FantasyTeam> {
  return client.get<FantasyTeam>(`/teams/${id}`);
}

/**
 * Update a team (rename, change scoring profile, or swap the kicker slot for
 * a second FLEX). Switching to a second FLEX is refused with 422
 * `slot_occupied` while a kicker still sits in the K slot.
 */
export async function updateTeam(
  id: number,
  data: {
    name?: string;
    scoring_profile_id?: number;
    flex_instead_of_kicker?: boolean;
  }
): Promise<FantasyTeam> {
  return client.patch<FantasyTeam>(`/teams/${id}`, data);
}

/**
 * Delete a team.
 */
export async function deleteTeam(id: number): Promise<null> {
  return client.del<null>(`/teams/${id}`);
}

/**
 * Get the roster for a specific week (default: current week).
 */
export async function getTeamRoster(
  team_id: number,
  week?: number,
  page?: number,
  per_page?: number
): Promise<Paginated<RosterEntry>> {
  const params = new URLSearchParams();
  if (week !== undefined) params.append("week", String(week));
  if (page !== undefined) params.append("page", String(page));
  if (per_page !== undefined) params.append("per_page", String(per_page));
  const queryString = params.toString();
  const path = queryString
    ? `/teams/${team_id}/roster?${queryString}`
    : `/teams/${team_id}/roster`;
  return client.get<Paginated<RosterEntry>>(path);
}

/**
 * Add a player to the team roster.
 */
export async function addRosterEntry(
  team_id: number,
  data: {
    gsis_id: string;
    slot: string;
    from_week: number;
  }
): Promise<RosterEntry> {
  return client.post<RosterEntry>(`/teams/${team_id}/roster`, data);
}

/**
 * Update a roster entry (move to different slot).
 */
export async function updateRosterEntry(
  team_id: number,
  entry_id: number,
  data: {
    slot: string;
  }
): Promise<RosterEntry> {
  return client.patch<RosterEntry>(
    `/teams/${team_id}/roster/${entry_id}`,
    data
  );
}

/**
 * Drop a player from the roster as of a specific week.
 */
export async function dropRosterEntry(
  team_id: number,
  entry_id: number,
  week: number
): Promise<null> {
  return client.del<null>(
    `/teams/${team_id}/roster/${entry_id}?week=${week}`
  );
}

/**
 * Get the full transaction history for a team.
 */
export async function getTeamTimeline(
  team_id: number
): Promise<RosterTimeline> {
  return client.get<RosterTimeline>(`/teams/${team_id}/timeline`);
}

/**
 * Get optimal vs. actual lineup for a specific week.
 */
export async function getTeamLineup(
  team_id: number,
  week?: number
): Promise<LineupOptimal> {
  const params = new URLSearchParams();
  if (week !== undefined) params.append("week", String(week));
  const queryString = params.toString();
  const path = queryString
    ? `/teams/${team_id}/lineup?${queryString}`
    : `/teams/${team_id}/lineup`;
  return client.get<LineupOptimal>(path);
}

// ============================================================================
// Scoring Profiles
// ============================================================================

/**
 * List all scoring profiles (presets + user's custom profiles).
 */
export async function listScoringProfiles(
  page?: number,
  per_page?: number
): Promise<Paginated<ScoringProfile>> {
  const params = new URLSearchParams();
  if (page !== undefined) params.append("page", String(page));
  if (per_page !== undefined) params.append("per_page", String(per_page));
  const queryString = params.toString();
  const path = queryString
    ? `/scoring-profiles?${queryString}`
    : "/scoring-profiles";
  return client.get<Paginated<ScoringProfile>>(path);
}

/**
 * Create a custom scoring profile.
 */
export async function createScoringProfile(data: {
  name: string;
  rules: Record<string, unknown>;
}): Promise<ScoringProfile> {
  return client.post<ScoringProfile>("/scoring-profiles", data);
}

/**
 * Update a custom scoring profile.
 */
export async function updateScoringProfile(
  id: number,
  data: {
    name?: string;
    rules?: Record<string, unknown>;
  }
): Promise<ScoringProfile> {
  return client.patch<ScoringProfile>(`/scoring-profiles/${id}`, data);
}

/**
 * Delete a custom scoring profile.
 */
export async function deleteScoringProfile(id: number): Promise<null> {
  return client.del<null>(`/scoring-profiles/${id}`);
}

// ============================================================================
// Stats
// ============================================================================

/**
 * Get league leaders for a specific stat.
 */
export async function getLeaders(
  params: StatsLeadersParams
): Promise<Paginated<Player>> {
  const searchParams = new URLSearchParams();
  searchParams.append("stat", params.stat);
  if (params.position !== undefined) {
    searchParams.append("position", params.position);
  }
  if (params.week !== undefined) {
    searchParams.append("week", String(params.week));
  }
  if (params.season !== undefined) {
    searchParams.append("season", String(params.season));
  }
  if (params.limit !== undefined) {
    searchParams.append("limit", String(params.limit));
  }
  const queryString = searchParams.toString();
  return client.get<Paginated<Player>>(`/stats/leaders?${queryString}`);
}

/**
 * Get all weeks with data for a season (drives week pickers).
 */
export async function getWeeks(season: number): Promise<Game[]> {
  return client.get<Game[]>(`/stats/weeks?season=${season}`);
}

// ============================================================================
// Meta
// ============================================================================

/**
 * Get freshness/staleness info for all data sources.
 */
export async function getFreshness(): Promise<Freshness> {
  return client.get<Freshness>("/meta/freshness");
}

/**
 * Get health status of the API and database.
 */
export async function getHealth(): Promise<HealthStatus> {
  return client.get<HealthStatus>("/meta/health");
}
